import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
} from "@mui/material";
import { format, isBefore, startOfDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";
import DeleteIcon16pxSvg from "../../../assets/images/icons/DeleteIcon16pxSvg";
import DefaultTooltip, { LeaveUsedDaysTooltipContent } from "../../../components/DefaultTooltip";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { getLeaveByUser, getLeaveByUserByLeaveId } from "../../../axios/User/UserApi";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  ControlpanelSuccess3thButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import { useLoading } from "../../../context/LoadingContext";
import { useAuth } from "../../../context/AuthContext";
import { getListUserGroup } from "../../../axios/Group/GroupUrlApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
import ErrorIcon from '@mui/icons-material/Error';

dayjs.extend(utc);
dayjs.extend(timezone);

let periodIdCounter = 0;
const createPeriodId = () => `period-${++periodIdCounter}`;
const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

/** Chuyển chuỗi thời gian HH:mm:ss sang phút (giữ phần giây dưới dạng số thập phân) */
const parseTimeStringToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  return h * 60 + m + (Number.isFinite(s) ? s / 60 : 0);
};

/** Chuyển chuỗi thời gian HH:mm:ss sang tổng giây */
const parseTimeStringToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  return (
    (Number.isFinite(h) ? h : 0) * 3600 +
    (Number.isFinite(m) ? m : 0) * 60 +
    (Number.isFinite(s) ? s : 0)
  );
};

/** Chuẩn hóa hoursPerLeaveDay về "HH:mm:ss" */
const normalizeHoursPerLeaveDay = (value) => {
  if (!value) return "08:00:00";
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || "08:00:00";
  }
  if (typeof value === "object") {
    const hour = String(value.hour ?? 0).padStart(2, "0");
    const minute = String(value.minute ?? 0).padStart(2, "0");
    const second = String(value.second ?? 0).padStart(2, "0");
    return `${hour}:${minute}:${second}`;
  }
  return "08:00:00";
};

/** Phút (có thể lẻ giây) → "HH:mm:ss" (giờ có thể > 24, giữ giây) */
const minutesToHhMmSs = (totalMinutes) => {
  const totalSeconds = Math.max(
    0,
    Math.round((Number(totalMinutes) || 0) * 60)
  );
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/** Format số giờ/ngày theo cấu hình leaveType hiển thị trong tooltip */
const formatHoursOneDayLabel = (value) => {
  if (!value) return "8";
  if (String(value).includes(":")) {
    const totalMinutes = parseTimeStringToMinutes(value);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) return String(hours);
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  }
  return String(value);
};

/** Chuyển tổng phút sang { days, hours, minutes, seconds } theo số giờ/ngày cấu hình */
const minutesToDurationParts = (totalMinutes, minutesPerDay) => {
  const dayMins =
    Number(minutesPerDay) > 0 ? Number(minutesPerDay) : DEFAULT_FULL_DAY_MINUTES;
  const totalSeconds = Math.max(0, Math.round((Number(totalMinutes) || 0) * 60));
  const daySeconds = Math.round(dayMins * 60);
  const days = Math.floor(totalSeconds / daySeconds);
  let rem = totalSeconds % daySeconds;
  const hours = Math.floor(rem / 3600);
  rem %= 3600;
  const minutes = Math.floor(rem / 60);
  const seconds = rem % 60;
  return { days, hours, minutes, seconds };
};

/** Format duration hiển thị trên UI (có giây) */
const formatDurationLabel = (parts, t) => {
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = parts || {};
  const hasHms = hours > 0 || minutes > 0 || seconds > 0;

  if (days > 0 && hasHms) {
    return t("leave-request.duration-days-hours-mins-secs", {
      days,
      hours,
      mins: minutes,
      secs: seconds,
    });
  }
  if (days > 0) {
    return t("leave-request.duration-days", { days });
  }
  if (hours > 0) {
    return t("leave-request.duration-hours-mins-secs", {
      hours,
      mins: minutes,
      secs: seconds,
    });
  }
  if (minutes > 0) {
    return t("leave-request.duration-mins-secs", {
      mins: minutes,
      secs: seconds,
    });
  }
  if (seconds > 0) {
    return t("leave-request.duration-secs", { secs: seconds });
  }
  return t("leave-request.duration-mins", { mins: 0 });
};

/** Lấy tên hiển thị loại phép theo ngôn ngữ hiện tại */
const getLeaveTypeDisplayName = (leaveTypesData, leaveTypeId) => {
  const option = leaveTypesData?.find(
    (item) => String(item.leaveTypeId) === String(leaveTypeId)
  );
  if (!option) return "—";
  const langId = Number(localStorage.getItem("i18nextLngId")) || 1;
  if (langId === 1) return option.remark || option.leaveTypeName || "—";
  if (langId === 2) return option.remarkEN || option.leaveTypeName || "—";
  return option.leaveTypeName || "—";
};

/** Tính số phút overlap giữa hai khoảng thời gian */
const getOverlapMinutes = (startA, endA, startB, endB) => {
  const overlapStart = Math.max(startA.getTime(), startB.getTime());
  const overlapEnd = Math.min(endA.getTime(), endB.getTime());
  if (overlapEnd <= overlapStart) return 0;
  return (overlapEnd - overlapStart) / (1000 * 60);
};

/** Hai khoảng nghỉ trùng nhau nếu giao nhau theo ngày lịch (không xét giờ) */
const doLeavePeriodsOverlapByDay = (startA, endA, startB, endB) => {
  if (!startA || !endA || !startB || !endB) return false;

  const a0 = dayjs(startA).startOf("day").valueOf();
  const a1 = dayjs(endA).startOf("day").valueOf();
  const b0 = dayjs(startB).startOf("day").valueOf();
  const b1 = dayjs(endB).startOf("day").valueOf();

  if ([a0, a1, b0, b1].some((v) => Number.isNaN(v))) return false;

  return a0 <= b1 && b0 <= a1;
};

/** Trả về true nếu có bất kỳ cặp period nào trùng ngày */
const hasOverlappingLeavePeriods = (periods = []) => {
  for (let i = 0; i < periods.length; i++) {
    for (let j = i + 1; j < periods.length; j++) {
      const a = periods[i];
      const b = periods[j];
      if (
        doLeavePeriodsOverlapByDay(
          a.startDate,
          a.endDate,
          b.startDate,
          b.endDate
        )
      ) {
        return true;
      }
    }
  }
  return false;
};

/** Tính tổng phút break time trùng với khoảng nghỉ trong một ngày */
const getBreakOverlapMinutes = (rangeStart, rangeEnd, breakTimes, dayDate, combineDateAndTime) => {
  if (!breakTimes?.length) return 0;

  let breakMinutes = 0;
  for (const breakTime of breakTimes) {
    if (!breakTime?.breakStart || !breakTime?.durationHours) continue;

    const breakStart = combineDateAndTime(dayDate, breakTime.breakStart);
    const durationMin = parseTimeStringToMinutes(breakTime.durationHours);
    const breakEnd = dayjs(breakStart).add(durationMin, "minute").toDate();

    breakMinutes += getOverlapMinutes(rangeStart, rangeEnd, breakStart, breakEnd);
  }
  return breakMinutes;
};

/**
 * Tìm thời điểm sau khi cộng `targetWorkMinutes` phút làm việc
 * (bỏ qua khoảng nghỉ giữa ca) trong [rangeStart, rangeEnd].
 */
const findTimeAfterWorkMinutes = (
  rangeStart,
  rangeEnd,
  targetWorkMinutes,
  breakTimes,
  dayDate,
  combineDateAndTime
) => {
  if (!rangeStart || !rangeEnd) return rangeStart;
  if (targetWorkMinutes <= 0) return rangeStart;

  const breaks = [];
  if (breakTimes?.length) {
    for (const breakTime of breakTimes) {
      if (!breakTime?.breakStart || !breakTime?.durationHours) continue;
      const breakStart = combineDateAndTime(dayDate, breakTime.breakStart);
      const durationMin = parseTimeStringToMinutes(breakTime.durationHours);
      const breakEnd = dayjs(breakStart).add(durationMin, "minute").toDate();
      breaks.push({ start: breakStart, end: breakEnd });
    }
    breaks.sort((a, b) => a.start - b.start);
  }

  let cursor = dayjs(rangeStart);
  const end = dayjs(rangeEnd);
  let remaining = targetWorkMinutes;

  while (cursor.isBefore(end) && remaining > 0) {
    const coveringBreak = breaks.find(
      (b) => !cursor.isBefore(dayjs(b.start)) && cursor.isBefore(dayjs(b.end))
    );
    if (coveringBreak) {
      cursor = dayjs(coveringBreak.end);
      continue;
    }

    const nextBreak = breaks.find((b) => dayjs(b.start).isAfter(cursor));
    const segmentEnd =
      nextBreak && dayjs(nextBreak.start).isBefore(end)
        ? dayjs(nextBreak.start)
        : end;

    const segmentMinutes = segmentEnd.diff(cursor, "minute", true);
    if (segmentMinutes <= remaining + 1e-9) {
      remaining -= segmentMinutes;
      cursor = segmentEnd;
      if (nextBreak && remaining > 0) {
        cursor = dayjs(nextBreak.end);
      }
    } else {
      cursor = cursor.add(remaining, "minute");
      remaining = 0;
    }
  }

  return cursor.toDate();
};

/** Làm tròn số ngày phép (2 chữ số thập phân): 1, 0.50, 0.63, ... */
const roundNumOfDay = (value) => Math.round(Number(value || 0) * 100) / 100;

/** Hiển thị số thập phân tối đa 2 chữ số, số nguyên giữ nguyên */
const formatDecimalNumber = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return value ?? 0;
  if (Number.isInteger(numberValue)) return numberValue;

  return Number(numberValue.toFixed(2));
};

/** Làm tròn 2 chữ số thập phân (vd: 2.50, 3) */
const formatOneDecimal = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 0;
  const rounded = Math.round(numberValue * 100) / 100;
  return Number.isInteger(rounded) ? rounded : rounded;
};

/**
 * usedFromYear (HH:mm:ss hoặc số ngày legacy) / hoursPerLeaveDay → số ngày
 */
const parseUsedFromYearToDays = (usedFromYear, hoursPerLeaveDay) => {
  if (usedFromYear == null || usedFromYear === "") return 0;

  const asString = String(usedFromYear).trim();
  if (asString.includes(":")) {
    const timeMins = parseTimeStringToMinutes(asString);
    const dayMins =
      parseTimeStringToMinutes(hoursPerLeaveDay) || DEFAULT_FULL_DAY_MINUTES;
    if (dayMins <= 0) return 0;
    return timeMins / dayMins;
  }

  const asNumber = Number(usedFromYear);
  return Number.isFinite(asNumber) ? asNumber : 0;
};

/** Hiển thị số ngày phép từ usedFromCarry/usedFromYear (HH:mm:ss hoặc số) */
const formatLeaveBalanceDisplay = (value, hoursPerLeaveDay) =>
  formatDecimalNumber(parseUsedFromYearToDays(value, hoursPerLeaveDay));

/** Snapshot từng bản ghi; fallback hoursPerLeaveDay */
const resolveItemHoursPerLeaveDay = (item) =>
  normalizeHoursPerLeaveDay(
    item?.hoursPerLeaveDaySnapshot || item?.hoursPerLeaveDay || "08:00:00"
  );

/** Phần còn lại: totalEntitlement − usedFromYear, tính theo giây để không mất giây lẻ */
const formatLeaveEntitlementAvailabilityText = (items, t) => {
  const list = toLeaveBalanceItems(items);
  if (!list.length) {
    return t("common.leave-entitlement-exhausted");
  }

  const hoursPerDay = resolveItemHoursPerLeaveDay(list[0]);
  const daySeconds =
    parseTimeStringToSeconds(hoursPerDay) || DEFAULT_FULL_DAY_MINUTES * 60;
  const totalDays = Number(list[0].totalEntitlementYear) || 0;
  const totalSeconds = Math.round(totalDays * daySeconds);

  const usedSeconds = list.reduce((sum, item) => {
    const usedRaw = item.usedFromYear;
    if (usedRaw == null || usedRaw === "") return sum;

    const asString = String(usedRaw).trim();
    if (asString.includes(":")) {
      return sum + parseTimeStringToSeconds(asString);
    }

    const days = Number(usedRaw);
    if (!Number.isFinite(days)) return sum;
    const itemDaySecs =
      parseTimeStringToSeconds(resolveItemHoursPerLeaveDay(item)) ||
      DEFAULT_FULL_DAY_MINUTES * 60;
    return sum + Math.round(days * itemDaySecs);
  }, 0);

  const remainingSeconds = totalSeconds - usedSeconds;
  if (!(remainingSeconds > 0)) {
    return t("common.leave-entitlement-exhausted");
  }

  const days = Math.floor(remainingSeconds / daySeconds);
  let rem = remainingSeconds % daySeconds;
  const hours = Math.floor(rem / 3600);
  rem %= 3600;
  const minutes = Math.floor(rem / 60);
  const seconds = rem % 60;

  return t("common.leave-entitlement-remaining", {
    days,
    hours,
    minutes,
    seconds,
  });
};

const toLeaveBalanceItems = (leaveBalance) => {
  if (!leaveBalance) return [];
  return Array.isArray(leaveBalance) ? leaveBalance.filter(Boolean) : [leaveBalance];
};

/** SUM(usedFromYear / hoursPerLeaveDaySnapshot) — cùng leaveTypeId, snapshot khác nhau */
const sumUsedFromYearDays = (items) =>
  toLeaveBalanceItems(items).reduce(
    (sum, item) =>
      sum +
      parseUsedFromYearToDays(item.usedFromYear, resolveItemHoursPerLeaveDay(item)),
    0
  );

const sumUsedFromCarryDays = (items) =>
  toLeaveBalanceItems(items).reduce(
    (sum, item) =>
      sum +
      parseUsedFromYearToDays(item.usedFromCarry, resolveItemHoursPerLeaveDay(item)),
    0
  );

const buildUsedFromYearDetails = (items) =>
  toLeaveBalanceItems(items).map((item) => {
    const hoursPerDay = resolveItemHoursPerLeaveDay(item);
    const usedRaw = item.usedFromYear;
    const usedTime =
      typeof usedRaw === "string" && String(usedRaw).includes(":")
        ? normalizeHoursPerLeaveDay(usedRaw)
        : minutesToHhMmSs(
            parseUsedFromYearToDays(usedRaw, hoursPerDay) *
              (parseTimeStringToMinutes(hoursPerDay) || DEFAULT_FULL_DAY_MINUTES)
          );
    return {
      usedTime,
      hoursPerDay,
      days: formatOneDecimal(parseUsedFromYearToDays(usedRaw, hoursPerDay)),
    };
  });

/** Phút làm việc đầy đủ 1 ngày theo ca (đã trừ break) */
const getFullShiftDayMinutes = (shift, combineDateAndTime, refDate, fallbackMinutes) => {
  if (!shift) return fallbackMinutes || 8 * 60;

  const shiftStart = shift.shiftStart || "00:00:00";
  const shiftEnd = shift.shiftEnd || "23:59:00";
  let dayStart = combineDateAndTime(refDate, shiftStart);
  let dayEnd = combineDateAndTime(refDate, shiftEnd);

  if (!dayStart || !dayEnd) return fallbackMinutes || 8 * 60;

  if (dayjs(dayEnd).isBefore(dayjs(dayStart))) {
    dayEnd = dayjs(dayEnd).add(1, "day").toDate();
  }

  let minutes = (dayEnd.getTime() - dayStart.getTime()) / (1000 * 60);

  if (shift?.breakTimes?.length) {
    minutes -= getBreakOverlapMinutes(
      dayStart,
      dayEnd,
      shift.breakTimes,
      refDate,
      combineDateAndTime
    );
  }

  const result = Math.max(0, Math.round(minutes));
  return result > 0 ? result : fallbackMinutes || 8 * 60;
};

/**
 * Tách period thành từng ngày kèm dayUnits (quy đổi từ phút) theo hoursPerLeaveDay của leaveType
 * (phút nghỉ thực tế vẫn giao với ca; 1 ngày phép = hoursPerLeaveDay phút, tối đa 1 ngày/ngày lịch)
 */
const getPeriodDayBreakdown = (start, end, shift, combineDateAndTime, hoursPerLeaveDayMinutes) => {
  if (!start || !end) return [];

  const startDt = dayjs(start);
  const endDt = dayjs(end);
  if (!startDt.isValid() || !endDt.isValid() || endDt.isBefore(startDt)) return [];

  const shiftStart = shift?.shiftStart || "00:00:00";
  const shiftEnd = shift?.shiftEnd || "23:59:00";
  const dayUnitMinutes =
    hoursPerLeaveDayMinutes > 0
      ? hoursPerLeaveDayMinutes
      : DEFAULT_FULL_DAY_MINUTES;
  const days = [];

  let cursor = startDt.startOf("day");
  const lastDay = endDt.startOf("day");

  while (cursor.isBefore(lastDay) || cursor.isSame(lastDay, "day")) {
    const dayDate = cursor.toDate();
    const dayStart = combineDateAndTime(dayDate, shiftStart);
    let dayEnd = combineDateAndTime(dayDate, shiftEnd);

    if (dayjs(dayEnd).isBefore(dayjs(dayStart))) {
      dayEnd = dayjs(dayEnd).add(1, "day").toDate();
    }

    // range* / leave* = giao giữa giờ user nhập và khung ca (hỗ trợ ca qua đêm)
    const rangeStart = startDt.isAfter(dayjs(dayStart)) ? startDt.toDate() : dayStart;
    const rangeEnd = endDt.isBefore(dayjs(dayEnd)) ? endDt.toDate() : dayEnd;
    const leaveStart = rangeStart;
    const leaveEnd = rangeEnd;

    if (rangeEnd > rangeStart) {
      let dayMinutes = (rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60);

      if (shift?.breakTimes?.length) {
        dayMinutes -= getBreakOverlapMinutes(
          rangeStart,
          rangeEnd,
          shift.breakTimes,
          dayDate,
          combineDateAndTime
        );
      }

      // Giữ độ chính xác giây; so sánh theo giây để không làm tròn lên đủ 1 ngày
      dayMinutes = Math.max(0, dayMinutes);
      const daySeconds = Math.round(dayMinutes * 60);
      const dayUnitSeconds = Math.round(dayUnitMinutes * 60);
      // Cap 1 ngày phép / ngày lịch (vd ca 00:00–23:59 không thành 3 ngày khi hoursPerLeaveDay=8h)
      const cappedSeconds = Math.min(daySeconds, dayUnitSeconds);
      const cappedMinutes = cappedSeconds / 60;
      // Chỉ đủ 1 ngày khi giây >= hoursPerLeaveDay; dưới ngưỡng không round lên 1.00
      const dayUnits =
        cappedSeconds >= dayUnitSeconds
          ? 1
          : Math.min(0.99, roundNumOfDay(cappedMinutes / dayUnitMinutes));

      if (cappedSeconds > 0) {
        days.push({
          date: cursor.format("YYYY-MM-DD"),
          year: cursor.year(),
          minutes: cappedMinutes,
          dayUnits,
          rangeStart,
          rangeEnd,
          leaveStart,
          leaveEnd,
        });
      }
    }

    cursor = cursor.add(1, "day");
  }

  return days;
};

/** Chia ngày paid / unpaid theo số ngày phép còn lại (ưu tiên theo thứ tự thời gian).
 * Nếu còn phép < 1 ngày trong một ngày ca → cắt giữa ngày (paid rồi unpaid), không đẩy cả ngày sang unpaid.
 */
const splitDaysByPaidBalance = (
  dayBreakdown,
  availablePaidDays,
  shift,
  combineDateAndTime,
  fallbackMinutes
) => {
  let remaining = Number(availablePaidDays);
  if (!Number.isFinite(remaining)) {
    return { paidDays: [...dayBreakdown], unpaidDays: [] };
  }

  const paidDays = [];
  const unpaidDays = [];

  for (const day of dayBreakdown) {
    if (remaining <= 1e-9) {
      unpaidDays.push(day);
      continue;
    }

    if (day.dayUnits <= remaining + 1e-9) {
      paidDays.push(day);
      remaining = roundNumOfDay(remaining - day.dayUnits);
      continue;
    }

    // Còn phép một phần ngày → tách paid/unpaid trong cùng ngày
    const fullDayMinutes =
      day.dayUnits > 0
        ? Math.round(day.minutes / day.dayUnits)
        : fallbackMinutes || DEFAULT_FULL_DAY_MINUTES;

    const paidMinutesNeeded = Math.max(
      0,
      Math.min(day.minutes, Math.round(remaining * fullDayMinutes))
    );

    if (paidMinutesNeeded <= 0) {
      unpaidDays.push(day);
      remaining = 0;
      continue;
    }

    if (paidMinutesNeeded >= day.minutes) {
      paidDays.push(day);
      remaining = 0;
      continue;
    }

    const dayDate = dayjs(day.date).toDate();
    const splitAt = findTimeAfterWorkMinutes(
      day.leaveStart,
      day.leaveEnd,
      paidMinutesNeeded,
      shift?.breakTimes,
      dayDate,
      combineDateAndTime
    );

    const unpaidMinutes = day.minutes - paidMinutesNeeded;

    paidDays.push({
      ...day,
      leaveEnd: splitAt,
      rangeEnd: splitAt,
      minutes: paidMinutesNeeded,
      dayUnits: roundNumOfDay(paidMinutesNeeded / fullDayMinutes),
    });

    unpaidDays.push({
      ...day,
      leaveStart: splitAt,
      rangeStart: splitAt,
      minutes: unpaidMinutes,
      dayUnits: roundNumOfDay(unpaidMinutes / fullDayMinutes),
    });

    remaining = 0;
  }

  return { paidDays, unpaidDays };
};

/** Tính số ngày phép còn lại + carry từ entitlement (gộp mọi bản ghi cùng loại) */
const getRemainingBalanceFromEntitlement = (leaveBalance) => {
  const items = toLeaveBalanceItems(leaveBalance);
  if (!items.length) {
    return { remainingPaidDays: 0, carryAvailable: 0 };
  }

  const first = items[0];
  const usedFromYear = sumUsedFromYearDays(items);
  const totalEntitlementYear = Number(first.totalEntitlementYear) || 0;

  let carryAvailable = 0;
  if (first?.isCarry) {
    let expiredOk = true;
    if (first?.expiredDt) {
      const now = new Date();
      const expiredDate = new Date(first.expiredDt);
      now.setHours(0, 0, 0, 0);
      expiredDate.setHours(0, 0, 0, 0);
      expiredOk = now <= expiredDate;
    }
    if (expiredOk) {
      carryAvailable = Math.max(
        (Number(first.carryIn) || 0) - sumUsedFromCarryDays(items),
        0
      );
    }
  }

  return {
    remainingPaidDays:
      Math.max(totalEntitlementYear - usedFromYear, 0) + carryAvailable,
    carryAvailable,
  };
};

/**
 * Phần vượt phép: ưu tiên trừ leaveToBeDeducted nếu còn phép,
 * phần còn lại mới ghi nhận unpaid (leaveTypeId = 1)
 */
const splitExcessByDeductLeave = (
  excessDays,
  deductRemainingDays,
  shift,
  combineDateAndTime,
  fallbackMinutes
) => {
  if (!excessDays?.length) {
    return { bridgeDays: [], unpaidDays: [] };
  }
  if (!(Number(deductRemainingDays) > 0)) {
    return { bridgeDays: [], unpaidDays: [...excessDays] };
  }
  const { paidDays: bridgeDays, unpaidDays } = splitDaysByPaidBalance(
    excessDays,
    deductRemainingDays,
    shift,
    combineDateAndTime,
    fallbackMinutes
  );
  return { bridgeDays, unpaidDays };
};

/**
 * Tính các field thời gian numOfTime* trực tiếp từ tổng phút (days[].minutes) theo năm,
 * không đi qua numOfDay. Carry (tính bằng ngày) được quy đổi sang phút rồi phân bổ
 * ưu tiên cho năm end trước, còn lại mới sang năm start.
 */
const buildTimeCountFields = (days, carryAvailableDays, hoursPerLeaveDayMinutes) => {
  const dayUnitMinutes =
    hoursPerLeaveDayMinutes > 0
      ? hoursPerLeaveDayMinutes
      : DEFAULT_FULL_DAY_MINUTES;

  if (!days?.length) {
    return {
      numOfTime: minutesToHhMmSs(0),
      numOfTimeStart: minutesToHhMmSs(0),
      numOfTimeEnd: minutesToHhMmSs(0),
      numOfTimeCarryStart: minutesToHhMmSs(0),
      numOfTimeCarryEnd: minutesToHhMmSs(0),
      carryUsedDays: 0,
      dayUnits: 0,
    };
  }

  const startYear = days[0].year;
  const endYear = days[days.length - 1].year;
  let minutesStart = 0;
  let minutesEnd = 0;

  if (startYear === endYear) {
    // Cùng năm: toàn bộ số phút nằm ở năm start, end không cần tính
    minutesStart = days.reduce((sum, d) => sum + (Number(d.minutes) || 0), 0);
    minutesEnd = 0;
  } else {
    // Khác năm: cộng phút theo năm start / năm end
    days.forEach((d) => {
      const mins = Number(d.minutes) || 0;
      if (d.year === startYear) minutesStart += mins;
      else minutesEnd += mins;
    });
  }

  const totalMinutes = minutesStart + minutesEnd;
  const dayUnits = roundNumOfDay(totalMinutes / dayUnitMinutes);

  // Ưu tiên dùng phép tồn (carry) trước — gán End rồi mới Start
  const carryAvailableMinutes = Math.max(
    Math.round((Number(carryAvailableDays) || 0) * dayUnitMinutes),
    0
  );
  let carryMinutesLeft = carryAvailableMinutes;
  const carryMinutesEnd = Math.min(carryMinutesLeft, minutesEnd);
  carryMinutesLeft -= carryMinutesEnd;
  const carryMinutesStart = Math.min(carryMinutesLeft, minutesStart);
  const carryUsedMinutes = carryMinutesStart + carryMinutesEnd;
  const carryUsedDays = roundNumOfDay(carryUsedMinutes / dayUnitMinutes);

  return {
    numOfTime: minutesToHhMmSs(totalMinutes),
    numOfTimeStart: minutesToHhMmSs(minutesStart),
    numOfTimeEnd: minutesToHhMmSs(minutesEnd),
    numOfTimeCarryStart: minutesToHhMmSs(carryMinutesStart),
    numOfTimeCarryEnd: minutesToHhMmSs(carryMinutesEnd),
    carryUsedDays,
    dayUnits,
  };
};

/** Tạo 1 leaveRequest từ danh sách ngày đã chọn (payload dùng numOfTime*) */
const buildLeaveRequestItem = ({
  days,
  userId,
  shiftId,
  leaveTypeId,
  requestReason,
  carryAvailable,
  toUTC,
  hoursPerLeaveDaySnapshot,
}) => {
  if (!days?.length) return null;

  const hoursPerLeaveDayMinutes =
    parseTimeStringToMinutes(hoursPerLeaveDaySnapshot) || DEFAULT_FULL_DAY_MINUTES;

  // Gửi đúng giờ user nhập
  const start = days[0].leaveStart ?? days[0].rangeStart;
  const end = days[days.length - 1].leaveEnd ?? days[days.length - 1].rangeEnd;
  const startIso = toUTC(start);
  const endIso = toUTC(end);
  // Cộng trực tiếp từ minutes của từng ngày (không quy đổi qua numOfDay)
  const counts = buildTimeCountFields(days, carryAvailable, hoursPerLeaveDayMinutes);

  return {
    requestType: "ADD_LEAVE",
    userId: Number(userId),
    start: startIso,
    end: endIso,
    requestReason: requestReason || "",
    newData: {
      userId: Number(userId),
      shiftId: shiftId != null ? Number(shiftId) : null,
      start: startIso,
      end: endIso,
      leaveTypeId: Number(leaveTypeId),
      leavePolicyId: null,
      numOfTime: counts.numOfTime,
      numOfTimeStart: counts.numOfTimeStart,
      numOfTimeCarryStart: counts.numOfTimeCarryStart,
      numOfTimeEnd: counts.numOfTimeEnd,
      numOfTimeCarryEnd: counts.numOfTimeCarryEnd,
      hoursPerLeaveDaySnapshot: normalizeHoursPerLeaveDay(hoursPerLeaveDaySnapshot),
      requestReason: requestReason || "",
    },
    // nội bộ: dùng để trừ carry/balance giữa các request, sẽ strip trước khi gửi
    _carryUsed: counts.carryUsedDays,
    _dayUnits: counts.dayUnits,
  };
};

/**
 * Tính toán phân bổ có lương / không lương tạm thời
 * theo số phút/ngày của ca làm việc
 */
const calculateLeaveAllocation = ({
  totalMinutes,
  minutesPerDay,
  usedDays,
  totalEntitlement,
  lateEarlyMinutes,
}) => {
  const fullDaysFromRequest = Math.floor(totalMinutes / minutesPerDay);
  const remainderMinutes = totalMinutes % minutesPerDay;

  const availableDays = Math.max(totalEntitlement - usedDays, 0);

  const paidDays = Math.min(fullDaysFromRequest, availableDays);
  const unpaidFromDays = fullDaysFromRequest - paidDays;
  let remainingBalance = availableDays - paidDays;

  const totalExtraMinutes = remainderMinutes + lateEarlyMinutes;
  let convertedDays = 0;
  let timeLeftMinutes = totalExtraMinutes;

  if (totalExtraMinutes >= minutesPerDay) {
    convertedDays = Math.floor(totalExtraMinutes / minutesPerDay);
    timeLeftMinutes = totalExtraMinutes % minutesPerDay;
  }

  const paidFromConversion = Math.min(convertedDays, remainingBalance);
  const unpaidFromConversion = convertedDays - paidFromConversion;
  remainingBalance -= paidFromConversion;

  return {
    requested: minutesToDurationParts(totalMinutes, minutesPerDay),
    paid: {
      days: paidDays + paidFromConversion,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
    unpaid: {
      days: unpaidFromDays + unpaidFromConversion,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
    timeLeft: minutesToDurationParts(timeLeftMinutes, minutesPerDay),
    availableDays,
    hasExcess: unpaidFromDays + unpaidFromConversion > 0,
  };
};

const AddLeaveDialog = (props) => {
  const {
    open,
    handleClose,
    setLoading,
    onRefresh,
    selectedDate,
    dateTimeFormat,
    leaveTypesData,
    shiftsData,
    policiesList,
    setTabValue,
  } = props;
  const { t } = useTranslation();
  const [show, hide] = useLoading();
  const { addLeaveRequest } = LeaveApi();
  const {
    isAdmin,
    genaralSettingDateFormat,
    defaultReason,
    profile,
    genaralSettingTimeZone,
    leaveToBeDeducted,
  } = useAuth();

  const [datepickerKey, setDatepickerKey] = useState();
  const MAX_REASON_LENGTH = 255;

  const sortedShifts = useMemo(
    () => [...shiftsData].sort((a, b) => b.shiftId - a.shiftId),
    [shiftsData]
  );

  // Không cho chọn leaveTypeId = 1 (AR / hourly) trên dropdown
  const selectableLeaveTypes = useMemo(
    () => (leaveTypesData || []).filter((item) => Number(item.leaveTypeId) !== 1),
    [leaveTypesData]
  );

  const [leaveByUser, setLeaveByUserData] = useState([]);
  const [leaveByUserByLeave, setLeaveByUserByLeaveData] = useState([]);
  const [userGroup, setUserGroup] = useState([]);
  const [loadingLeaveEntitlement, setLoadingLeaveEntitlement] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lateEarlyUsedTime =
    leaveByUser.find((item) => item.leaveTypeId === 1)?.usedFromYear || "00:00:00";

  const [leavePeriods, setLeavePeriods] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        group: 1,
        pageNo: 1,
        recordPerPage: 50000,
        langId: localStorage.getItem("i18nextLngId"),
      };
      show();
      try {
        const response = await getListUserGroup(payload);
        const raw = response.data.data || [];
        const mapped = raw.map((u) => ({
          ...u,
          userId: u.userId ?? u.id,
        }));
        setUserGroup(mapped);
      } catch (error) {
        if (error.response) {
          if (error.response.status !== 401) {
            toast.error(t(error?.response.data.message));
          }
        } else {
          toast.error(t("common.network-timeout"));
        }
      } finally {
        hide();
      }
    };

    fetchData();
  }, [t, show, hide]);

  const initialFormData = {
    userId: "",
    requestStartDate: "",
    requestEndDate: "",
    leaveType: leaveByUser[0]?.leaveTypeId,
    leavePolicy: policiesList[0]?.leavePolicyId,
    requestReason: "",
    responseReason: "" || defaultReason,
    user: null,
  };

  const [formData, setFormData] = useState(initialFormData);

  const toDate = (v) => {
    if (!v) return new Date(NaN);
    if (dayjs.isDayjs(v)) return v.toDate();
    if (v instanceof Date) return v;
    return new Date(v);
  };

  const combineDateAndTime = useCallback(
    (dateInput, timeStr) => {
      if (!dateInput || !timeStr) return null;

      const [hh, mm, ss] = timeStr.split(":").map(Number);

      return dayjs(dateInput)
        .tz(genaralSettingTimeZone, true)
        .hour(hh)
        .minute(mm)
        .second(ss || 0)
        .millisecond(0)
        .toDate();
    },
    [genaralSettingTimeZone]
  );

  const applyShiftToDate = useCallback(
    (baseDate, shift) => {
      if (!shift) {
        return {
          start: combineDateAndTime(baseDate, "00:00:00"),
          end: combineDateAndTime(baseDate, "23:59:00"),
        };
      }

      const start = combineDateAndTime(baseDate, shift.shiftStart);
      let end = combineDateAndTime(baseDate, shift.shiftEnd);

      if (shift.shiftEnd === "00:00:00" || isBefore(end, start)) {
        end = dayjs(end).add(1, "day").toDate();
      }

      return { start, end };
    },
    [combineDateAndTime]
  );

  const applyShiftToPeriod = useCallback(
    (periodStart, periodEnd, shift) => {
      const startDay = startOfDay(toDate(periodStart));
      const endDay = startOfDay(toDate(periodEnd || periodStart));

      if (!shift) {
        return {
          start: combineDateAndTime(startDay, "00:00:00"),
          end: combineDateAndTime(endDay, "23:59:00"),
        };
      }

      const start = combineDateAndTime(startDay, shift.shiftStart);
      let end = combineDateAndTime(endDay, shift.shiftEnd);
      const isSameDay = startDay.getTime() === endDay.getTime();

      if (isSameDay) {
        if (shift.shiftEnd === "00:00:00" || isBefore(end, start)) {
          end = dayjs(end).add(1, "day").toDate();
        }
      } else if (shift.shiftEnd === "00:00:00") {
        end = dayjs(endDay).add(1, "day").toDate();
      }

      return { start, end };
    },
    [combineDateAndTime]
  );

  const createDefaultPeriod = useCallback(
    (shift = sortedShifts[0]) => {
      const baseDate = startOfDay(toDate(selectedDate));
      const { start, end } = applyShiftToDate(baseDate, shift);
      return {
        id: createPeriodId(),
        shiftId: shift?.shiftId ?? null,
        startDate: start,
        endDate: end,
      };
    },
    [sortedShifts, selectedDate, applyShiftToDate]
  );

  // Khởi tạo khoảng thời gian mặc định khi mở dialog
  useEffect(() => {
    if (!open) return;
    if (!sortedShifts.length) return;

    setLeavePeriods([createDefaultPeriod()]);
    setDatepickerKey((k) => (k || 0) + 1);
  }, [open, sortedShifts, createDefaultPeriod]);

  useEffect(() => {
    if (!userGroup.length || !profile) return;

    const currentUser = userGroup.find((u) => u.id === profile.userId);

    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        user: currentUser,
        userName: currentUser.fullName,
        userId: currentUser.id,
      }));
    }
  }, [userGroup, profile]);

  const fetchLeaveByUserData = async (userId) => {
    if (!userId) return;

    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      userId: userId,
      year: new Date().getFullYear(),
    };

    try {
      const response = await getLeaveByUser(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveByUserData(response.data.data ?? []);
      }
    } catch (error) {
      if (error?.response && error.response.status !== 401) {
        toast.error(error?.response?.data?.message || "Error");
      } else {
        toast.error(t("common.network-timeout"));
      }
    }
  };

  const fetchLeaveByUserByLeaveId = async (userId, leaveTypeId, year) => {
    if (!userId || !leaveTypeId) return;

    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      userId: userId,
      leaveTypeId: leaveTypeId,
      year: year || new Date().getFullYear(),
    };

    show();
    setLeaveByUserByLeaveData([]);
    setLoadingLeaveEntitlement(true);

    try {
      const response = await getLeaveByUserByLeaveId(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveByUserByLeaveData(response.data.data ?? null);
      }
    } catch (error) {
      if (error?.response && error.response.status !== 401) {
        toast.error(error?.response?.data?.message || "Error");
      } else {
        toast.error(t("common.network-timeout"));
      }
    } finally {
      setLoadingLeaveEntitlement(false);
      hide();
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!policiesList?.length) return;

    setFormData((prev) => {
      if (prev.leavePolicy) return prev;
      return {
        ...prev,
        leavePolicy: policiesList[0].leavePolicyId,
      };
    });
  }, [policiesList, open]);

  useEffect(() => {
    if (!open) return;

    setFormData((prev) => ({
      ...prev,
      leaveType: selectableLeaveTypes[0]?.leaveTypeId,
    }));
  }, [selectedDate, selectableLeaveTypes, open]);

  const [isSelectUserError, setIsSelectUserError] = useState(false);
  const [isSelectValid, setIsSelectValid] = useState(false);
  const [isInputValid, setIsInputValid] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [validationMessages, setValidationMessages] = useState({
    userId: "",
    requestEndDate: "",
  });

  const toUTCByBrowserTimezone = (date) => {
    if (!date) return null;
    const browserTZ = dayjs.tz.guess();
    return dayjs(date).tz(browserTZ, true).utc().format();
  };

  const syncFormDatesFromPeriods = (periods) => {
    const validPeriods = periods.filter((p) => p.startDate && p.endDate);
    if (!validPeriods.length) return { requestStartDate: "", requestEndDate: "" };

    const starts = validPeriods.map((p) => toDate(p.startDate).getTime());
    const ends = validPeriods.map((p) => toDate(p.endDate).getTime());

    return {
      requestStartDate: new Date(Math.min(...starts)),
      requestEndDate: new Date(Math.max(...ends)),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const invalidPeriod = leavePeriods.find((p) => {
      if (!p.startDate || !p.endDate) return true;
      return isBefore(toDate(p.endDate), toDate(p.startDate));
    });

    if (invalidPeriod) {
      toast.warn(t("common.invalid-date"));
      return;
    }

    // if (hasOverlappingLeavePeriods(leavePeriods)) {
    //   toast.warn(t("leave-request.overlapping-periods"));
    //   return;
    // }

    if (!formData.userId) {
      toast.warn(t("common.select-user") || "Please select user");
      return;
    }

    const leaveBalance = leaveByUserByLeave?.[0];
    const usedFromYear = sumUsedFromYearDays(leaveByUserByLeave);
    const totalEntitlementYear = Number(leaveBalance?.totalEntitlementYear) || 0;

    let carryAvailable = 0;
    if (leaveBalance?.isCarry && leaveBalance?.expiredDt) {
      const now = new Date();
      const expiredDate = new Date(leaveBalance.expiredDt);
      now.setHours(0, 0, 0, 0);
      expiredDate.setHours(0, 0, 0, 0);
      if (now <= expiredDate) {
        carryAvailable = Math.max(
          (Number(leaveBalance.carryIn) || 0) - sumUsedFromCarryDays(leaveByUserByLeave),
          0
        );
      }
    } else if (leaveBalance?.isCarry) {
      carryAvailable = Math.max(
        (Number(leaveBalance.carryIn) || 0) - sumUsedFromCarryDays(leaveByUserByLeave),
        0
      );
    }

    // leaveTypeId = 1: không tách unpaid; còn lại: balance = năm còn lại + carry
    const isHourlyLeaveType = Number(formData.leaveType) === 1;
    const selectedLeaveSnapshot = resolveHoursPerLeaveDaySnapshot(formData.leaveType);
    let remainingPaidDays = isHourlyLeaveType
      ? Number.POSITIVE_INFINITY
      : Math.max(totalEntitlementYear - usedFromYear, 0) + carryAvailable;

    let remainingCarry = isHourlyLeaveType ? 0 : carryAvailable;

    // Ưu tiên overflowLeaveTypeId của loại phép đang chọn (khớp summary UI);
    // fallback setting công ty leaveToBeDeducted nếu chưa cấu hình overflow
    const selectedLeaveTypeConfig = (leaveTypesData || []).find(
      (lt) => Number(lt.leaveTypeId) === Number(formData.leaveType)
    );
    const overflowFromEntitlement = Number(leaveBalance?.overflowLeaveTypeId);
    const overflowFromLeaveType = Number(
      selectedLeaveTypeConfig?.overflowLeaveTypeId
    );
    const deductLeaveTypeId = [
      overflowFromEntitlement,
      overflowFromLeaveType,
      Number(leaveToBeDeducted),
    ].find((id) => Number.isFinite(id) && id > 0) ?? 0;
    const canUseDeductLeave =
      !isHourlyLeaveType &&
      Number.isFinite(deductLeaveTypeId) &&
      deductLeaveTypeId > 0 &&
      deductLeaveTypeId !== 1 &&
      deductLeaveTypeId !== Number(formData.leaveType);

    let remainingDeductDays = 0;
    let remainingDeductCarry = 0;
    if (canUseDeductLeave) {
      const deductBalance = leaveByUser.filter(
        (item) => Number(item.leaveTypeId) === deductLeaveTypeId
      );
      const deductRemaining = getRemainingBalanceFromEntitlement(deductBalance);
      remainingDeductDays = deductRemaining.remainingPaidDays;
      remainingDeductCarry = deductRemaining.carryAvailable;
    }

    const leaveRequests = [];

    for (const period of leavePeriods) {
      const shift = getShiftForPeriod(period);
      const dayBreakdown = getPeriodDayBreakdown(
        period.startDate,
        period.endDate,
        shift,
        combineDateAndTime,
        minutesPerDay
      );

      if (!dayBreakdown.length) continue;

      const { paidDays, unpaidDays: excessDays } = splitDaysByPaidBalance(
        dayBreakdown,
        remainingPaidDays,
        shift,
        combineDateAndTime,
        minutesPerDay
      );

      if (paidDays.length) {
        const paidRequest = buildLeaveRequestItem({
          days: paidDays,
          userId: formData.userId,
          shiftId: period.shiftId,
          leaveTypeId: formData.leaveType,
          requestReason: formData.requestReason,
          carryAvailable: remainingCarry,
          toUTC: toUTCByBrowserTimezone,
          hoursPerLeaveDaySnapshot: resolveHoursPerLeaveDaySnapshot(
            formData.leaveType
          ),
        });

        if (paidRequest) {
          remainingCarry = roundNumOfDay(
            Math.max(remainingCarry - (paidRequest._carryUsed || 0), 0)
          );
          remainingPaidDays = Number.isFinite(remainingPaidDays)
            ? roundNumOfDay(
                Math.max(remainingPaidDays - (paidRequest._dayUnits || 0), 0)
              )
            : remainingPaidDays;

          const { _carryUsed, _dayUnits, ...cleanPaid } = paidRequest;
          leaveRequests.push(cleanPaid);
        }
      }

      // Phần vượt phép loại đang chọn → ưu tiên leaveToBeDeducted, còn lại mới unpaid
      if (excessDays.length && !isHourlyLeaveType) {
        const deductSnapshot = resolveHoursPerLeaveDaySnapshot(deductLeaveTypeId);
        const deductMinutesPerDay =
          parseTimeStringToMinutes(deductSnapshot) || DEFAULT_FULL_DAY_MINUTES;

        // Remap excess theo hoursPerLeaveDay của loại bridge trước khi tách
        const remappedExcess = excessDays.map((d) => ({
          ...d,
          dayUnits: roundNumOfDay(
            (Number(d.minutes) || 0) / deductMinutesPerDay
          ),
        }));

        const { bridgeDays, unpaidDays } = splitExcessByDeductLeave(
          remappedExcess,
          canUseDeductLeave ? remainingDeductDays : 0,
          shift,
          combineDateAndTime,
          deductMinutesPerDay
        );

        if (bridgeDays.length) {
          const bridgeRequest = buildLeaveRequestItem({
            days: bridgeDays,
            userId: formData.userId,
            shiftId: period.shiftId,
            leaveTypeId: deductLeaveTypeId,
            requestReason: formData.requestReason,
            carryAvailable: remainingDeductCarry,
            toUTC: toUTCByBrowserTimezone,
            hoursPerLeaveDaySnapshot: deductSnapshot,
          });

          if (bridgeRequest) {
            remainingDeductCarry = roundNumOfDay(
              Math.max(
                remainingDeductCarry - (bridgeRequest._carryUsed || 0),
                0
              )
            );
            remainingDeductDays = roundNumOfDay(
              Math.max(
                remainingDeductDays - (bridgeRequest._dayUnits || 0),
                0
              )
            );

            const { _carryUsed, _dayUnits, ...cleanBridge } = bridgeRequest;
            leaveRequests.push(cleanBridge);
          }
        }

        if (unpaidDays.length) {
          const unpaidRequest = buildLeaveRequestItem({
            days: unpaidDays,
            userId: formData.userId,
            shiftId: period.shiftId,
            leaveTypeId: 1,
            requestReason: formData.requestReason,
            carryAvailable: 0,
            toUTC: toUTCByBrowserTimezone,
            hoursPerLeaveDaySnapshot: selectedLeaveSnapshot,
          });

          if (unpaidRequest) {
            const { _carryUsed, _dayUnits, ...cleanUnpaid } = unpaidRequest;
            leaveRequests.push(cleanUnpaid);
          }
        }
      }
    }

    const payload = {
      leaveRequests,
      langId: localStorage.getItem("i18nextLngId"),
    };

    if (!leaveRequests.length) {
      toast.warn(t("common.select-date-range") || t("common.invalid-date"));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await addLeaveRequest(payload);
      if (
        response.data.message === "Successful" ||
        response.data.message === "Thành công"
      ) {
        handleCloseDialog();
        toast.success(t("common.add-leave-successfully"));
        onRefresh?.();
        setTabValue?.(1);
      } else if (response.data.responseCode === 1085) {
        handleCloseDialog();
        toast.success(t("common.add-leave-successfully"));
        onRefresh?.();
        setTabValue?.(1);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error.response?.data?.responseCode === 1085) {
        handleCloseDialog();
        toast.success(t("common.add-leave-successfully"));
        onRefresh?.();
        setTabValue?.(1);
      } else {
        toast.error(
          error.response?.data?.message ||
            error.response?.data ||
            t("common.network-timeout")
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    handleClose();
    setFormData(initialFormData);
    setIsSelectUserError(false);
    setHasUserInteracted(false);
    setSelectedValue(null);
    setResetTrigger((prev) => !prev);
    setLeavePeriods([]);
    setDatepickerKey();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "requestReason" && value.length > MAX_REASON_LENGTH) {
      return;
    }

    if (name === "leaveType") {
      const valueNum = Number(value);
      setLeaveByUserByLeaveData([]);
      setFormData((prev) => ({
        ...prev,
        leaveType: valueNum,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePeriodShiftChange = (periodId, shiftId) => {
    const selectedShift = sortedShifts.find(
      (s) => String(s.shiftId) === String(shiftId)
    );

    setLeavePeriods((prev) =>
      prev.map((period) => {
        if (period.id !== periodId) return period;
        if (!selectedShift) return { ...period, shiftId };

        const { start, end } =
          period.startDate && period.endDate
            ? applyShiftToPeriod(
                period.startDate,
                period.endDate,
                selectedShift
              )
            : applyShiftToDate(
                startOfDay(toDate(period.startDate || selectedDate)),
                selectedShift
              );

        return {
          ...period,
          shiftId: selectedShift.shiftId,
          startDate: start,
          endDate: end,
        };
      })
    );
  };

  const handlePeriodDateChange = (periodId, startDate, endDate) => {
    const nextStart = toDate(startDate);
    const nextEnd = toDate(endDate);

    // Không cho 2+ khoảng nghỉ trùng ngày lịch
    const overlapsOther = leavePeriods.some((period) => {
      if (period.id === periodId) return false;
      if (!period.startDate || !period.endDate) return false;
      return doLeavePeriodsOverlapByDay(
        nextStart,
        nextEnd,
        period.startDate,
        period.endDate
      );
    });

    // if (overlapsOther) {
    //   toast.warn(t("leave-request.overlapping-periods"));
    //   return;
    // }

    setLeavePeriods((prev) =>
      prev.map((period) =>
        period.id === periodId
          ? { ...period, startDate: nextStart, endDate: nextEnd }
          : period
      )
    );
  };

  const handleAddPeriod = () => {
    setLeavePeriods((prev) => {
      const shift = sortedShifts[0];

      // Period mới bắt đầu sau ngày kết thúc muộn nhất để tránh trùng ngày mặc định
      let baseDate = startOfDay(toDate(selectedDate));
      const validEnds = prev
        .filter((p) => p.endDate)
        .map((p) => dayjs(toDate(p.endDate)).startOf("day").valueOf());

      if (validEnds.length) {
        baseDate = dayjs(Math.max(...validEnds)).add(1, "day").startOf("day").toDate();
      }

      const { start, end } = applyShiftToDate(baseDate, shift);

      return [
        ...prev,
        {
          id: createPeriodId(),
          shiftId: shift?.shiftId ?? null,
          startDate: start,
          endDate: end,
        },
      ];
    });
  };

  const handleRemovePeriod = (periodId) => {
    setLeavePeriods((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((p) => p.id !== periodId);
    });
  };

  const getShiftForPeriod = (period) =>
    sortedShifts.find((s) => String(s.shiftId) === String(period.shiftId));

  /** Lấy hoursPerLeaveDay của leaveType (snapshot gửi API) */
  const resolveHoursPerLeaveDaySnapshot = useCallback(
    (leaveTypeId) => {
      const fromTypes = (leaveTypesData || []).find(
        (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
      );
      const fromBalance = (leaveByUser || []).find(
        (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
      );
      return normalizeHoursPerLeaveDay(
        fromTypes?.hoursPerLeaveDay ??
          fromBalance?.hoursPerLeaveDay ??
          fromBalance?.hoursPerLeaveDaySnapshot ??
          "08:00:00"
      );
    },
    [leaveTypesData, leaveByUser]
  );

  // 1 ngày phép = hoursPerLeaveDay của leaveType đang chọn (không theo độ dài ca)
  const minutesPerDay = useMemo(() => {
    const snapshot = resolveHoursPerLeaveDaySnapshot(formData.leaveType);
    return parseTimeStringToMinutes(snapshot) || DEFAULT_FULL_DAY_MINUTES;
  }, [formData.leaveType, resolveHoursPerLeaveDaySnapshot]);

  const hoursOneDayLabel = useMemo(
    () => formatHoursOneDayLabel(minutesPerDay / 60),
    [minutesPerDay]
  );

  /** SUM(usedFromYear / snapshot); kèm phần còn khả dụng (ngày/giờ/phút/giây) */
  const leaveEntitlementInfo = useMemo(() => {
    if (!leaveByUserByLeave?.length) {
      return {
        text: "—",
        usedDays: null,
        details: [],
      };
    }

    const details = buildUsedFromYearDetails(leaveByUserByLeave);
    const usedDaysExact = sumUsedFromYearDays(leaveByUserByLeave);
    const usedDays = formatOneDecimal(usedDaysExact);
    const totalDays = formatDecimalNumber(
      leaveByUserByLeave[0].totalEntitlementYear
    );
    const availability = formatLeaveEntitlementAvailabilityText(
      leaveByUserByLeave,
      t
    );

    return {
      text: `${usedDays}/${totalDays} (${availability})`,
      usedDays,
      details,
    };
  }, [leaveByUserByLeave, t]);

  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (hasUserInteracted) {
      if (formData.userId === "") {
        setValidationMessages((prevMessages) => ({
          ...prevMessages,
          userId: "User is required",
        }));
      } else {
        setValidationMessages((prevMessages) => ({
          ...prevMessages,
          userId: "",
        }));
      }
    }
  }, [formData.userId, hasUserInteracted]);

  useEffect(() => {
    if (hasUserInteracted) {
      const { userId } = formData;
      const isUserIdValid = userId !== "" && validationMessages.userId === "";
      isUserIdValid ? setIsSelectUserError(null) : setIsSelectUserError(true);
      setIsSelectValid(isUserIdValid);
    }
  }, [formData.userId, validationMessages.userId, hasUserInteracted]);

  useEffect(() => {
    const allValid = leavePeriods.every((p) => {
      if (!p.startDate || !p.endDate) return false;
      return !isBefore(toDate(p.endDate), toDate(p.startDate));
    });
    // const noOverlap = !hasOverlappingLeavePeriods(leavePeriods);
    setIsInputValid(allValid && leavePeriods.length > 0);
  }, [leavePeriods]);

  useEffect(() => {
    const dates = syncFormDatesFromPeriods(leavePeriods);
    setFormData((prev) => ({ ...prev, ...dates }));
  }, [leavePeriods]);

  const autoFillCurrentUser = () => {
    if (!userGroup.length || !profile) return;

    const currentUser = userGroup.find((u) => u.id === profile.userId);
    if (!currentUser) return;

    setFormData((prev) => {
      if (prev.userId) return prev;
      return {
        ...prev,
        user: currentUser,
        userName: currentUser.fullName,
        userId: currentUser.id,
      };
    });
  };

  useEffect(() => {
    if (!open) return;
    autoFillCurrentUser();
  }, [open, userGroup, profile]);

  useEffect(() => {
    if (!open) return;
    if (!formData.userId) return;
    fetchLeaveByUserData(formData.userId);
  }, [formData.userId, open]);

  useEffect(() => {
    if (!open) return;
    if (!formData.userId || !formData.leaveType) return;

    // Có 2 năm (start ≠ end) → lấy năm end; cùng năm thì start/end giống nhau
    const period = leavePeriods[0];
    const startYear = period?.startDate
      ? toDate(period.startDate).getFullYear()
      : null;
    const endYear = period?.endDate
      ? toDate(period.endDate).getFullYear()
      : null;

    const year =
      endYear != null && startYear != null && endYear !== startYear
        ? endYear
        : endYear ?? startYear ?? new Date().getFullYear();

    fetchLeaveByUserByLeaveId(formData.userId, formData.leaveType, year);
  }, [formData.userId, formData.leaveType, open, leavePeriods]);

  useEffect(() => {
    setLeaveByUserData([]);
    setLeaveByUserByLeaveData([]);
  }, [formData.userId]);

  const carryLeaveText = useMemo(() => {
    if (!leaveByUserByLeave?.length) return null;

    const item = leaveByUserByLeave[0];
    if (!item.isCarry) return null;

    if (item.expiredDt) {
      const now = new Date();
      const expiredDate = new Date(item.expiredDt);
      now.setHours(0, 0, 0, 0);
      expiredDate.setHours(0, 0, 0, 0);
      if (now > expiredDate) return null;
    }

    const expired = item.expiredDt
      ? format(new Date(item.expiredDt), genaralSettingDateFormat)
      : "";
    const hoursPerDay = resolveItemHoursPerLeaveDay(item);

    return `${formatLeaveBalanceDisplay(item.usedFromCarry, hoursPerDay)}/${formatLeaveBalanceDisplay(item.carryIn, hoursPerDay)} (${t("common.expire-on")} ${expired})`;
  }, [leaveByUserByLeave, t, genaralSettingDateFormat]);

  const isUserSelected = Boolean(formData.userId);

  const isCurrentUser =
    formData.userId &&
    profile?.userId &&
    String(formData.userId) === String(profile.userId);

  const isDeductLeaveType = false;

  // Hạn mức ngày nghỉ từ entitlement theo loại phép đã chọn
  const leaveLimits = useMemo(() => {
    const item =
      leaveByUserByLeave?.[0] ||
      leaveByUser.find(
        (l) => Number(l.leaveTypeId) === Number(formData.leaveType)
      );

    if (!item) return null;

    return {
      maxLeaveDuration: Number(item.maxLeaveDuration),
      isMonthlyLimit: Boolean(item.isMonthlyLimit),
      monthlyLimitDays: Number(item.monthlyLimitDays),
      isWeeklyLimit: Boolean(item.isWeeklyLimit),
      weeklyLimitDays: Number(item.weeklyLimitDays),
    };
  }, [leaveByUserByLeave, leaveByUser, formData.leaveType]);

  // Tính duration từng khoảng và tổng (phút đã cap theo hoursPerLeaveDay / ngày lịch)
  const periodDurations = useMemo(() => {
    return leavePeriods.map((period) => {
      const shift = getShiftForPeriod(period);
      const days = getPeriodDayBreakdown(
        period.startDate,
        period.endDate,
        shift,
        combineDateAndTime,
        minutesPerDay
      );
      const minutes = days.reduce((s, d) => s + (Number(d.minutes) || 0), 0);
      return minutesToDurationParts(minutes, minutesPerDay);
    });
  }, [leavePeriods, sortedShifts, combineDateAndTime, minutesPerDay]);

  const totalRequestedMinutes = useMemo(() => {
    return leavePeriods.reduce((sum, period) => {
      const shift = getShiftForPeriod(period);
      const days = getPeriodDayBreakdown(
        period.startDate,
        period.endDate,
        shift,
        combineDateAndTime,
        minutesPerDay
      );
      return sum + days.reduce((s, d) => s + (Number(d.minutes) || 0), 0);
    }, 0);
  }, [leavePeriods, sortedShifts, combineDateAndTime, minutesPerDay]);

  const totalRequestedDays = useMemo(() => {
    return roundNumOfDay(
      leavePeriods.reduce((sum, period) => {
        const shift = getShiftForPeriod(period);
        const days = getPeriodDayBreakdown(
          period.startDate,
          period.endDate,
          shift,
          combineDateAndTime,
          minutesPerDay
        );

        return sum + days.reduce((daySum, day) => daySum + day.dayUnits, 0);
      }, 0)
    );
  }, [leavePeriods, sortedShifts, combineDateAndTime, minutesPerDay]);

  const leaveAllocation = useMemo(() => {
    if (!isDeductLeaveType || !leaveByUserByLeave?.length) return null;
    if (totalRequestedMinutes <= 0) return null;

    const item = leaveByUserByLeave[0];
    const usedDays = sumUsedFromYearDays(leaveByUserByLeave);
    const totalEntitlement = Number(item.totalEntitlementYear) || 0;
    const lateEarlyMinutes = parseTimeStringToMinutes(lateEarlyUsedTime);

    return calculateLeaveAllocation({
      totalMinutes: totalRequestedMinutes,
      minutesPerDay,
      usedDays,
      totalEntitlement,
      lateEarlyMinutes,
    });
  }, [
    isDeductLeaveType,
    leaveByUserByLeave,
    totalRequestedMinutes,
    minutesPerDay,
    lateEarlyUsedTime,
    formData.leaveType,
    resolveHoursPerLeaveDaySnapshot,
  ]);

  const totalRequestedParts = useMemo(
    () => minutesToDurationParts(totalRequestedMinutes, minutesPerDay),
    [totalRequestedMinutes, minutesPerDay]
  );

  /** Kiểm tra vượt hạn mức theo số ngày đã quy đổi từ ca */
  const nonDeductEntitlement = useMemo(() => {
    if (isDeductLeaveType || formData.leaveType === 1) return null;
    if (!leaveByUserByLeave?.length || totalRequestedDays <= 0) return null;

    const item = leaveByUserByLeave[0];
    const usedDays = sumUsedFromYearDays(leaveByUserByLeave);
    const totalEntitlement = Number(item.totalEntitlementYear) || 0;
    const primaryBalance = getRemainingBalanceFromEntitlement(leaveByUserByLeave);
    const maxDays = primaryBalance.remainingPaidDays;
    const maxMinutes = Math.round(maxDays * minutesPerDay);
    const paidMinutes = Math.min(totalRequestedMinutes, maxMinutes);
    const excessMinutes = Math.max(totalRequestedMinutes - maxMinutes, 0);

    const deductLeaveTypeId = Number(item.overflowLeaveTypeId);
    const canUseDeductLeave =
      Number.isFinite(deductLeaveTypeId) &&
      deductLeaveTypeId > 0 &&
      deductLeaveTypeId !== 1 &&
      deductLeaveTypeId !== Number(formData.leaveType);

    let bridgeMinutes = 0;
    let unpaidMinutes = excessMinutes;

    if (canUseDeductLeave && excessMinutes > 0) {
      const deductBalance = leaveByUser.filter(
        (l) => Number(l.leaveTypeId) === deductLeaveTypeId
      );
      const deductRemaining = getRemainingBalanceFromEntitlement(deductBalance);
      const deductMaxMinutes = Math.round(
        deductRemaining.remainingPaidDays * minutesPerDay
      );
      bridgeMinutes = Math.min(excessMinutes, deductMaxMinutes);
      unpaidMinutes = Math.max(excessMinutes - bridgeMinutes, 0);
    }

    const remainingAfterMinutes = Math.max(maxMinutes - totalRequestedMinutes, 0);
    const isExceeded = excessMinutes > 0;

    return {
      isExceeded,
      maxDays,
      totalEntitlement,
      usedDays,
      bridgeMinutes,
      bridgeDays: roundNumOfDay(bridgeMinutes / minutesPerDay),
      deductLeaveTypeId: canUseDeductLeave ? deductLeaveTypeId : null,
      requestedLabel: formatDurationLabel(
        minutesToDurationParts(totalRequestedMinutes, minutesPerDay),
        t
      ),
      paidLabel: formatDurationLabel(
        minutesToDurationParts(paidMinutes, minutesPerDay),
        t
      ),
      bridgeLabel: formatDurationLabel(
        minutesToDurationParts(bridgeMinutes, minutesPerDay),
        t
      ),
      unpaidLabel: formatDurationLabel(
        minutesToDurationParts(unpaidMinutes, minutesPerDay),
        t
      ),
      timeLeftLabel: isExceeded
        ? formatDurationLabel(
            minutesToDurationParts(unpaidMinutes, minutesPerDay),
            t
          )
        : formatDurationLabel(
            minutesToDurationParts(remainingAfterMinutes, minutesPerDay),
            t
          ),
    };
  }, [
    isDeductLeaveType,
    formData.leaveType,
    leaveByUserByLeave,
    leaveByUser,
    leaveToBeDeducted,
    totalRequestedDays,
    totalRequestedMinutes,
    minutesPerDay,
    resolveHoursPerLeaveDaySnapshot,
    t,
  ]);

  const isNonDeductEntitlementExceeded = Boolean(nonDeductEntitlement?.isExceeded);

  const selectedLeaveTypeName = useMemo(
    () => getLeaveTypeDisplayName(leaveTypesData, formData.leaveType),
    [leaveTypesData, formData.leaveType]
  );

  const deductLeaveTypeName = useMemo(
    () =>
      nonDeductEntitlement?.deductLeaveTypeId
        ? getLeaveTypeDisplayName(
            leaveTypesData,
            nonDeductEntitlement.deductLeaveTypeId
          )
        : "",
    [leaveTypesData, nonDeductEntitlement?.deductLeaveTypeId]
  );

  /** Summary thống nhất cho mọi loại phép (trừ leave theo giờ) */
  const leaveSummary = useMemo(() => {
    if (formData.leaveType === 1) return null;
    if (totalRequestedMinutes <= 0) return null;

    if (isDeductLeaveType) {
      if (!leaveAllocation) return null;

      const hasUnpaid =
        leaveAllocation.unpaid.days > 0 ||
        leaveAllocation.unpaid.hours > 0 ||
        leaveAllocation.unpaid.minutes > 0 ||
        leaveAllocation.unpaid.seconds > 0;

      return {
        hasUnpaid,
        hasBridge: false,
        requestedLabel: formatDurationLabel(totalRequestedParts, t),
        paidLabel: formatDurationLabel(leaveAllocation.paid, t),
        bridgeLabel: null,
        unpaidLabel: formatDurationLabel(leaveAllocation.unpaid, t),
        timeLeftLabel: formatDurationLabel(leaveAllocation.timeLeft, t),
        footerType: hasUnpaid ? "deduct" : null,
        footerData: hasUnpaid
          ? {
              balance: leaveAllocation.availableDays,
              unpaid: leaveAllocation.unpaid.days,
            }
          : null,
      };
    }

    if (!nonDeductEntitlement) return null;

    const { isExceeded, bridgeMinutes = 0 } = nonDeductEntitlement;
    const hasBridge = bridgeMinutes > 0;
    return {
      hasUnpaid: isExceeded,
      hasBridge,
      requestedLabel: nonDeductEntitlement.requestedLabel,
      paidLabel: nonDeductEntitlement.paidLabel,
      bridgeLabel: hasBridge ? nonDeductEntitlement.bridgeLabel : null,
      unpaidLabel: nonDeductEntitlement.unpaidLabel,
      timeLeftLabel: nonDeductEntitlement.timeLeftLabel,
      footerType: isExceeded
        ? hasBridge
          ? "bridge"
          : "non-deduct"
        : null,
      footerData: isExceeded
        ? {
            ...nonDeductEntitlement,
            balance: nonDeductEntitlement.maxDays,
            unpaid: nonDeductEntitlement.unpaidLabel,
            bridge: nonDeductEntitlement.bridgeLabel,
            deductLeaveType: deductLeaveTypeName,
          }
        : null,
    };
  }, [
    formData.leaveType,
    totalRequestedMinutes,
    isDeductLeaveType,
    leaveAllocation,
    nonDeductEntitlement,
    totalRequestedParts,
    deductLeaveTypeName,
    t,
  ]);

  return (
    <CustomDialog
      maxWidth={"md"}
      open={open}
      loading={isSubmitting || loadingLeaveEntitlement}
      onClose={handleCloseDialog}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("button.add-leave")}</p>
          </div>
          <GrayIconButton
            onClick={handleCloseDialog}
            className="self-start"
            disabled={isSubmitting || loadingLeaveEntitlement}
          >
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>
      <DialogContent sx={{ mt: 0.5 }}>
        {/* Hàng 1: User | Loại phép */}
        <div className="pt-[10px] grid grid-cols-1 md:grid-cols-2 gap-2">
          <CustomInput
            required={false}
            title={t("common.leave-staff")}
            inputType="text"
            name="selectUser"
            value={formData.userName || formData.user?.fullName || ""}
            readOnly
            className="w-full"
          />
          <div>
            <label className="block text-sm text-[#171717] mb-2">
              {t("common.leave-type")}{" "}
              <span className="text-[#DC2626]">*</span>
            </label>
            <FormControl className="w-full">
              <CustomSelect
                name="leaveType"
                id="addLeaveType"
                value={isUserSelected ? formData.leaveType : ""}
                options={selectableLeaveTypes}
                onChange={handleInputChange}
                dataType="leaveType"
                disabled={!isUserSelected}
                readOnly={!isUserSelected}
              />
            </FormControl>
          </div>
        </div>

        {/* Hàng 2: Quyền lợi phép; bên dưới: phép mang sang */}
        {formData.leaveType !== 1 && (
          <div className="pt-2 flex flex-col gap-2">
            <div>
              <div className="flex items-center gap-1 mb-0">
                <label className="block text-sm">
                  {t("common.leave-entitlement")}
                </label>
                {leaveEntitlementInfo.details?.length ? (
                  <DefaultTooltip
                    title={t("common.leave-entitlement")}
                    readMore={false}
                    content={
                      <LeaveUsedDaysTooltipContent
                        details={leaveEntitlementInfo.details}
                        totalDays={leaveEntitlementInfo.usedDays}
                      />
                    }
                  >
                    <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5 cursor-help" />
                  </DefaultTooltip>
                ) : null}
              </div>
              <CustomInput
                required={false}
                title={t("common.leave-entitlement")}
                labelEnable={false}
                inputType="text"
                value={leaveEntitlementInfo.text}
                readOnly
                className="w-full"
                containerClassName="mt-2"
              />
            </div>
            {carryLeaveText && (
              <CustomInput
                required={false}
                title={t("common.carry-leave", {
                  year: leavePeriods[0]?.startDate
                    ? toDate(leavePeriods[0].startDate).getFullYear() - 1
                    : new Date().getFullYear() - 1,
                })}
                inputType="text"
                value={carryLeaveText}
                readOnly
                className="w-full"
              />
            )}
          </div>
        )}

        {/* Danh sách khoảng thời gian nghỉ */}
        <div className="mt-2 rounded-lg bg-[#F9FAFB] p-2 space-y-3">
          <p className="text-sm font-semibold text-[#171717]">
            {t("leave-request.leave-periods-title")}
          </p>

          {leavePeriods.map((period, index) => {
            const durationLabel = formatDurationLabel(
              periodDurations[index] || {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
              },
              t
            );

            const canRemovePeriod = leavePeriods.length > 1;
            const periodShift = getShiftForPeriod(period);
            const shiftMinDateTime = periodShift
              ? combineDateAndTime(
                  period.startDate || new Date(),
                  periodShift.shiftStart || "00:00:00"
                )
              : undefined;
            const shiftMaxDateTime = periodShift
              ? combineDateAndTime(
                  period.startDate || new Date(),
                  periodShift.shiftEnd || "23:59:00"
                )
              : undefined;

            return (
              <div
                key={period.id}
                className="rounded-lg border border-[#D1D5DB] bg-white"
              >
                <div className="flex justify-between items-center mb-1 bg-[#F5F8FF] rounded-t-lg p-2">
                  <p className="text-sm font-semibold text-[#171717]">
                    {t("leave-request.period-label", { index: index + 1 })}
                  </p>
                  <span className="text-xs font-medium text-[#525252] bg-[#F5F5F5] rounded-md px-3 py-1">
                    {durationLabel}
                  </span>
                </div>

                <div
                  className={`grid grid-cols-1 rounded-b-lg bg-white gap-2 items-end p-2 ${
                    canRemovePeriod
                      ? "md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]"
                      : "md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
                  }`}
                >
                  <div>
                    <label className="block text-sm text-[#171717] mb-2">
                      {t("common.shifts")}{" "}
                      <span className="text-[#DC2626]">*</span>
                    </label>
                    <FormControl className="w-full">
                      <CustomSelect
                        name={`shift-${period.id}`}
                        value={period.shiftId}
                        onChange={(e) =>
                          handlePeriodShiftChange(period.id, e.target.value)
                        }
                        options={sortedShifts}
                        dataType="shift"
                      />
                    </FormControl>
                  </div>
                  <div>
                    <label className="block text-sm text-[#171717] mb-2">
                      {t("common.date-time")}{" "}
                      <span className="text-[#DC2626]">*</span>
                    </label>
                    <CustomDateTimeRangePicker
                      key={`${datepickerKey}-${period.id}`}
                      value={[period.startDate, period.endDate]}
                      disableTime={false}
                      leaveLimits={leaveLimits}
                      minDateTime={shiftMinDateTime}
                      maxDateTime={shiftMaxDateTime}
                      onChange={([start, end]) => {
                        if (!start || !end) return;
                        handlePeriodDateChange(period.id, start, end);
                      }}
                      dateTimeFormat={dateTimeFormat}
                    />
                  </div>
                  {canRemovePeriod && (
                    <button
                      type="button"
                      onClick={() => handleRemovePeriod(period.id)}
                      className="flex items-center justify-center w-10 h-10 rounded-md border border-[#D00D00] hover:bg-red-50 transition shrink-0"
                    >
                      <DeleteIcon16pxSvg color="#D00D00" width={20} height={20} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <ControlpanelSuccess3thButton
            onClick={handleAddPeriod}
            className="w-full !justify-center"
          >
            <AddIcon fontSize="small" />
            <p>{t("leave-request.add-date-range")}</p>
          </ControlpanelSuccess3thButton>
        </div>

        {/* Bảng tính toán tạm thời - dùng chung cho mọi loại phép */}
        {leaveSummary && (
          <div className="pt-4">
            {(() => {
              const { hasUnpaid } = leaveSummary;
              const accentColor = hasUnpaid ? "#F97C00" : "#0057FF";
              const bgColor = hasUnpaid ? "#FEF2E6" : "#F0F9FF";

              const summaryColumns = [
                {
                  label: t("leave-request.total-leave-requested"),
                  value: leaveSummary.requestedLabel,
                  highlight: false,
                },
                {
                  label: t("leave-request.paid-with-leave-type", {
                    leaveType: selectedLeaveTypeName,
                  }),
                  value: leaveSummary.paidLabel,
                  highlight: false,
                },
                ...(leaveSummary.hasBridge
                  ? [
                      {
                        label: t("leave-request.paid-with-leave-type", {
                          leaveType: deductLeaveTypeName,
                        }),
                        value: leaveSummary.bridgeLabel,
                        highlight: false,
                      },
                    ]
                  : []),
                {
                  label: t("leave-request.unpaid-leave"),
                  value: leaveSummary.unpaidLabel,
                  highlight: true,
                },
              ];

              return (
                <div
                  className="rounded-md px-4 py-4 border-l-[6px]"
                  style={{
                    backgroundColor: bgColor,
                    borderLeftColor: accentColor,
                  }}
                >
                  <div className="w-full">
                    <div className="flex items-center gap-3 mb-3">
                      <ErrorIcon
                        sx={{ color: accentColor, fontSize: 25, flexShrink: 0 }}
                      />
                      <p className="text-sm font-semibold text-[#171717]">
                        {t("leave-request.exceeds-available-balance")}
                      </p>
                    </div>

                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex items-center w-full flex-col sm:flex-row sm:divide-x sm:divide-[#E5E7EB]">
                        {summaryColumns.map((col, idx) => (
                          <div
                            key={col.label}
                            className={`flex-1 min-w-0 py-1 text-center ${
                              idx > 0 ? "sm:pl-4" : ""
                            } ${
                              idx < summaryColumns.length - 1 ? "sm:pr-4" : ""
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <p
                                className="text-xs"
                                style={{
                                  color: col.highlight ? accentColor : "#737373",
                                }}
                              >
                                {col.label}
                              </p>
                              {col.showHelp && (
                                <DefaultTooltip
                                  title={t("leave-request.time-left")}
                                  readMore={false}
                                  content={
                                    <div className="flex flex-col gap-3">
                                      <p>
                                        {t("leave-request.time-left-tooltip-des", {
                                          hours: hoursOneDayLabel,
                                        })}
                                      </p>
                                    </div>
                                  }
                                >
                                  <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5" />
                                </DefaultTooltip>
                              )}
                            </div>
                            <p
                              className="text-sm font-semibold"
                              style={{
                                color: col.highlight ? accentColor : "#171717",
                              }}
                            >
                              {col.value}
                            </p>
                          </div>
                        ))}
                      </div>

                      {leaveSummary.footerType === "deduct" && leaveSummary.footerData && (
                        <p className="text-xs text-[#737373] mt-4">
                          {t("leave-request.balance-unpaid-summary", leaveSummary.footerData)}
                        </p>
                      )}

                      {leaveSummary.footerType === "bridge" && leaveSummary.footerData && (
                        <p className="text-xs text-[#737373] mt-4">
                          {t("leave-request.balance-bridge-unpaid-summary", {
                            balance: leaveSummary.footerData.balance,
                            bridge: leaveSummary.footerData.bridge,
                            deductLeaveType: leaveSummary.footerData.deductLeaveType,
                            unpaid: leaveSummary.footerData.unpaidLabel,
                          })}
                        </p>
                      )}

                      {leaveSummary.footerType === "non-deduct" && leaveSummary.footerData && (
                        <p className="text-xs text-[#737373] mt-4">
                          {t("leave-request.non-deduct-entitlement-exceeded-des", {
                            days: formatDecimalNumber(leaveSummary.footerData.maxDays),
                            total: formatDecimalNumber(leaveSummary.footerData.totalEntitlement),
                            used: formatDecimalNumber(leaveSummary.footerData.usedDays),
                            requested: leaveSummary.footerData.requestedLabel,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Reason */}
        <div className="pt-5">
          <CustomInput
            required={true}
            title={t("common.reason")}
            inputType="text"
            name="requestReason"
            placeholder={t("common.reason")}
            handleInputChange={handleInputChange}
            value={formData.requestReason}
            className="w-full"
          />
          {/* <div
            className={`flex justify-end text-xs mt-1 ${formData.requestReason.length >= MAX_REASON_LENGTH
              ? "text-red-500"
              : "text-gray-500"
              }`}
          >
            {formData.requestReason.length}/{MAX_REASON_LENGTH}
          </div> */}
        </div>

        {!isCurrentUser && (
          <div className="">
            <CustomInput
              required={true}
              title={t("common.approve-reason")}
              inputType="text"
              name="responseReason"
              placeholder={t("common.approve-reason")}
              handleInputChange={handleInputChange}
              value={formData.responseReason}
              className="w-full"
            />
            {/* <div
              className={`flex justify-end text-xs mt-1 ${formData.responseReason?.length >= MAX_REASON_LENGTH
                ? "text-red-500"
                : "text-gray-500"
                }`}
            >
              {formData.responseReason?.length || 0}/{MAX_REASON_LENGTH}
            </div> */}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton
            onClick={handleCloseDialog}
            disabled={isSubmitting}
          >
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !isInputValid ||
              !formData.userId ||
              !formData.requestReason ||
              (!isCurrentUser && !formData.responseReason)
            }
          >
            <p>{t("button.confirm")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default AddLeaveDialog;
